import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect, ChangeEvent, FormEvent, forwardRef } from "react";

import { SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";

// -------------------------------------------------------------------
// Framer Motion Transition Component for the Dialog
// -------------------------------------------------------------------
const MotionTransition = forwardRef<HTMLDivElement, any>(function MotionTransition(props, ref) {
  const { children, ...other } = props;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      {...other}
    >
      {children}
    </motion.div>
  );
});

// -------------------------------------------------------------------
// Types and Interfaces
// -------------------------------------------------------------------
export type FieldType =
  | "text"
  | "number"
  | "email"
  | "date"
  | "tel"
  | "select"
  | "textarea"
  | "checkbox";

export interface FieldConfig {
  key: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

export interface DynamicDataCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, any>) => void;
  initialData: Record<string, any>;
  fields: FieldConfig[];
  modalTitle?: string;
}

// -------------------------------------------------------------------
// Framer Motion Variants for Staggered Animations
// -------------------------------------------------------------------
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// -------------------------------------------------------------------
// DynamicDataCaptureModal Component
// -------------------------------------------------------------------
const DynamicDataCaptureModal: React.FC<DynamicDataCaptureModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  fields,
  modalTitle = "Aggiungi un lead",
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    const data: Record<string, any> = {};
    fields.forEach((field) => {
      data[field.key] = initialData && field.key in initialData ? initialData[field.key] : "";
    });
    return data;
  });

  // Update form data if initialData changes.
  useEffect(() => {
    const data: Record<string, any> = {};
    fields.forEach((field) => {
      data[field.key] = initialData && field.key in initialData ? initialData[field.key] : "";
    });
    setFormData(data);
  }, [initialData, fields]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Accept both standard HTML and MUI Select change events.
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target as { name: string; value: string };
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.required && !formData[field.key].toString().trim()) {
        newErrors[field.key] = `${field.label} is required`;
      }
      if (field.type === "email" && formData[field.key]) {
        const emailRegex = /^\S+@\S+\.\S+$/;
        if (!emailRegex.test(formData[field.key])) {
          newErrors[field.key] = "Valid email is required";
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
      onClose();
    }
  };

  const renderField = (field: FieldConfig) => {
    const commonProps = {
      name: field.key,
      label: field.label,
      value: formData[field.key],
      onChange: handleChange,
      fullWidth: true,
      placeholder: field.placeholder,
      error: Boolean(errors[field.key]),
      helperText: errors[field.key] || "",
    };

    switch (field.type) {
      case "select":
        return (
          <FormControl fullWidth error={Boolean(errors[field.key])}>
            <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
            <Select
              labelId={`${field.key}-label`}
              label={field.label}
              name={field.key}
              value={formData[field.key]}
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "textarea":
        return <TextField {...commonProps} multiline rows={4} />;
      case "checkbox":
        return <TextField {...commonProps} type="checkbox" />;
      default:
        return (
          <TextField
            {...commonProps}
            type={field.type || "text"}
            InputLabelProps={{
              sx: {
                transform: "translate(11px, 8px) scale(1)",
                transition: "all 0.2s ease",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
            }}
          />
        );
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog
          open={isOpen}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          TransitionComponent={MotionTransition}
          sx={{
            "& .MuiDialog-paper": {
              zIndex: 1, // Lower this
              position: "relative",
              width: "80%",
              minHeight: "50%",
              overflow: "visible",
            },
            "& .MuiPopover-root": {
              zIndex: 9999, // Make this significantly higher
            },
            "& .MuiMenu-paper": {
              zIndex: 9999, // Match this with the Popover
            },
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }}>
            {modalTitle}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ overflow: "visible" }}>
              <motion.div variants={containerVariants} initial="hidden" animate="visible">
                <Grid container spacing={2}>
                  {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.key}>
                      <motion.div variants={itemVariants}>{renderField(field)}</motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </DialogContent>
            <DialogActions>
              <SafeArgonButton onClick={onClose} color="secondary">
                Annulla
              </SafeArgonButton>
              <SafeArgonButton type="submit" variant="contained" color="primary">
                Aggiungi
              </SafeArgonButton>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default DynamicDataCaptureModal;
